/**
 * Utilities for normalizing freeform location search strings into the structured
 * filter params the events API understands.
 *
 * The backend stores US state as a 2-letter USPS abbreviation (e.g. "OR"),
 * so any full state name from the user ("Oregon", "Oregon USA") must be
 * converted before being sent as a query parameter.
 */

function titleCaseWords(str) {
  return str.replace(/\b\w+/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
}

const US_STATE_NAME_TO_ABBR = {
  'alabama': 'AL', 'alaska': 'AK', 'arizona': 'AZ', 'arkansas': 'AR',
  'california': 'CA', 'colorado': 'CO', 'connecticut': 'CT', 'delaware': 'DE',
  'florida': 'FL', 'georgia': 'GA', 'hawaii': 'HI', 'idaho': 'ID',
  'illinois': 'IL', 'indiana': 'IN', 'iowa': 'IA', 'kansas': 'KS',
  'kentucky': 'KY', 'louisiana': 'LA', 'maine': 'ME', 'maryland': 'MD',
  'massachusetts': 'MA', 'michigan': 'MI', 'minnesota': 'MN', 'mississippi': 'MS',
  'missouri': 'MO', 'montana': 'MT', 'nebraska': 'NE', 'nevada': 'NV',
  'new hampshire': 'NH', 'new jersey': 'NJ', 'new mexico': 'NM', 'new york': 'NY',
  'north carolina': 'NC', 'north dakota': 'ND', 'ohio': 'OH', 'oklahoma': 'OK',
  'oregon': 'OR', 'pennsylvania': 'PA', 'rhode island': 'RI', 'south carolina': 'SC',
  'south dakota': 'SD', 'tennessee': 'TN', 'texas': 'TX', 'utah': 'UT',
  'vermont': 'VT', 'virginia': 'VA', 'washington': 'WA', 'west virginia': 'WV',
  'wisconsin': 'WI', 'wyoming': 'WY', 'district of columbia': 'DC',
};

const US_STATE_ABBR_TO_NAME = Object.fromEntries(
  Object.entries(US_STATE_NAME_TO_ABBR).map(([name, abbr]) => [abbr, titleCaseWords(name)]),
);

const COUNTRY_SUFFIX_PATTERN =
  /,?\s*(USA|US|U\.S\.?|United\s+States(?:\s+of\s+America)?)$/i;

/** Normalize a token for country-alias comparison (e.g. "U.S." → "us"). */
function normalizeCountryToken(token) {
  return (token || '').trim().toLowerCase().replace(/\./g, '');
}

function isCountryToken(token) {
  const normalized = normalizeCountryToken(token);
  return (
    normalized === 'us' ||
    normalized === 'usa' ||
    normalized === 'united states' ||
    normalized === 'united states of america'
  );
}

/** Strip trailing US country qualifiers, including repeated segments. */
function stripCountrySuffix(raw) {
  let cleaned = raw.trim();
  while (COUNTRY_SUFFIX_PATTERN.test(cleaned)) {
    cleaned = cleaned.replace(COUNTRY_SUFFIX_PATTERN, '').trim();
  }
  return cleaned;
}

/**
 * Converts a full US state name to its 2-letter USPS abbreviation.
 * Returns the original string unchanged if no match is found.
 */
export function stateNameToAbbr(name) {
  if (!name) return name;
  const trimmed = name.trim();
  if (/^[A-Za-z]{2}$/.test(trimmed)) {
    return trimmed.toUpperCase();
  }
  return US_STATE_NAME_TO_ABBR[trimmed.toLowerCase()] || trimmed;
}

/**
 * Normalizes a freeform location string into structured filter params
 * the events API can use.
 *
 * Handles:
 *  - "Oregon USA"       → { state: "OR" }
 *  - "Oregon"           → { state: "OR" }
 *  - "OR"               → { state: "OR" }
 *  - "Oregon, U.S."     → { state: "OR" }
 *  - "Portland, Oregon" → { city: "Portland", state: "OR" }
 *  - "Portland, OR"     → { city: "Portland", state: "OR" }
 *  - "97201, OR"        → { state: "OR" }   (zip → state-only)
 *  - "Portland"         → { location_search: "Portland" }
 *
 * @param {string} raw - User-typed location string
 * @returns {{ city?: string, state?: string, location_search?: string }}
 */
export function normalizeLocationParams(raw) {
  if (!raw || !raw.trim()) return {};

  const cleaned = stripCountrySuffix(raw);
  if (!cleaned) return {};

  const parts = cleaned.split(',').map((p) => p.trim()).filter(Boolean);

  if (parts.length >= 2) {
    const cityPart = parts[0];
    const secondPart = parts[1];

    if (isCountryToken(secondPart)) {
      const stateAbbr = US_STATE_NAME_TO_ABBR[cityPart.toLowerCase()];
      if (stateAbbr) {
        return { state: stateAbbr };
      }
    }

    const statePart = stateNameToAbbr(secondPart);
    if (/^\d{5}$/.test(cityPart)) {
      return { state: statePart };
    }
    return { city: cityPart, state: statePart };
  }

  const single = parts[0];
  const abbr = US_STATE_NAME_TO_ABBR[single.toLowerCase()];
  if (abbr) {
    return { state: abbr };
  }

  if (/^[A-Za-z]{2}$/.test(single)) {
    return { state: single.toUpperCase() };
  }

  return { location_search: single };
}

/**
 * Human-readable label for active location filter chips.
 * Keeps API params as abbreviations while showing full names in the UI.
 */
export function formatLocationFilterLabel(raw) {
  if (!raw || !String(raw).trim()) return '';

  const cleaned = stripCountrySuffix(String(raw).trim());
  if (!cleaned) return '';

  const parts = cleaned.split(',').map((part) => part.trim()).filter(Boolean);

  if (parts.length >= 2) {
    const locality = parts[0];
    const statePart = parts[parts.length - 1];

    if (isCountryToken(statePart) && parts.length === 2) {
      const stateAbbr = US_STATE_NAME_TO_ABBR[locality.toLowerCase()];
      return stateAbbr ? US_STATE_ABBR_TO_NAME[stateAbbr] : locality;
    }

    const stateAbbr = stateNameToAbbr(statePart);
    const stateLabel = US_STATE_ABBR_TO_NAME[stateAbbr] || statePart;
    return `${locality}, ${stateLabel}`;
  }

  const single = parts[0];
  const stateAbbrFromName = US_STATE_NAME_TO_ABBR[single.toLowerCase()];
  if (stateAbbrFromName) {
    return US_STATE_ABBR_TO_NAME[stateAbbrFromName];
  }

  if (/^[A-Za-z]{2}$/.test(single)) {
    return US_STATE_ABBR_TO_NAME[single.toUpperCase()] || single.toUpperCase();
  }

  return cleaned;
}
